/**
 * Turns a failed handoff request into a reason string the error page can read.
 *
 * FastAPI's `detail` is usually a string, but the claim-link guard answers with
 * a structured one:
 *
 *   { message: "Handoff is no longer available (status: claimed)",
 *     current_status: "claimed",
 *     handoff: { ... } }
 *
 * Passing that straight through put "[object Object]" in the URL and threw the
 * status away. When a status is present it is normalised to the
 * `status:<status>` form `resolveHandoffError` understands, so the agent gets
 * copy naming the state instead of a generic failure.
 *
 * Kept free of the axios import so it can be unit-tested on its own.
 */

export interface HandoffApiErrorLike {
  response?: {
    data?: { detail?: unknown; error?: unknown; [key: string]: unknown };
    status?: number;
  };
  message?: string;
}

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

export const errorReason = (error: unknown, fallback: string): string => {
  const data = (error as HandoffApiErrorLike | undefined)?.response?.data;
  const detail = data?.detail;

  if (detail && typeof detail === 'object') {
    const structured = detail as { current_status?: unknown; message?: unknown };
    const status = nonEmptyString(structured.current_status);
    if (status) return `status:${status}`;
    const message = nonEmptyString(structured.message);
    if (message) return message;
  }

  return (
    nonEmptyString(detail) ??
    nonEmptyString(data?.error) ??
    nonEmptyString((error as HandoffApiErrorLike | undefined)?.message) ??
    fallback
  );
};
